import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Status/severity system (BUI Dual-Theme brief, Section 16): every variant is a themed tint +
 * matching text + a subtle matching border ("tinted background, subtle border, strong readable
 * text... do not rely on colour alone") rather than a solid-fill pill — colors never hardcoded
 * per-theme (no `dark:` overrides — this app switches themes via `[data-theme]` + CSS variables,
 * not Tailwind's class-based dark mode, so `dark:` variants here were always inert dead code).
 */
const badgeVariants = cva('inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium', {
  variants: {
    variant: {
      neutral: 'border-border bg-surface text-ink-muted',
      brand: 'border-brand-border bg-brand-subtle text-brand-hover',
      success: 'border-success/25 bg-success-soft text-success',
      warning: 'border-warning/25 bg-warning-soft text-warning',
      danger: 'border-danger/25 bg-danger-soft text-danger',
      // Operational "in progress" blue — distinct from the `info` severity variant below (which is
      // the grey "Informational" severity level, not this one).
      active: 'border-info/25 bg-info-soft text-info',
      critical: 'border-severity-critical/25 bg-severity-soft-critical text-severity-critical',
      high: 'border-severity-high/25 bg-severity-soft-high text-severity-high',
      medium: 'border-severity-medium/25 bg-severity-soft-medium text-severity-medium',
      low: 'border-severity-low/25 bg-severity-soft-low text-severity-low',
      info: 'border-severity-info/25 bg-severity-soft-info text-severity-info',
    },
  },
  defaultVariants: { variant: 'neutral' },
});

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

const SEVERITY_VARIANT = { critical: 'critical', high: 'high', medium: 'medium', low: 'low', info: 'info' } as const;

export function SeverityBadge({ severity }: { severity: keyof typeof SEVERITY_VARIANT }) {
  return (
    <Badge variant={SEVERITY_VARIANT[severity]} className="capitalize">
      {severity}
    </Badge>
  );
}
